export type EventProcessingMode = 'process-event' | 'site-check' | 'manual-patrol';

export interface ProcessingTimerSyncState {
  correlationId: string;
  entityId: string;
  entityType: EventProcessingMode;
  prevElapsedMs: number;
  segmentStartAt: string;
  running: boolean;
}

export interface PageSyncEvent {
  id: string;
  label: string;
  eventProcessingMode?: EventProcessingMode;
  processingTimer?: ProcessingTimerSyncState;
  /** Stable cross-app tab identity (instanceId in EventQ, correlationId in Resolve). */
  correlationId?: string;
  /** Site being processed (site-check / manual-patrol modes). Numeric site ID from the API. */
  siteId?: number;
}

export interface PageSyncState {
  activeEventId: string | null;
  events: PageSyncEvent[];
}

export const AppEventTypes = {
  STATE_SYNC: 'STATE_SYNC',
  STATE_QUERY: 'STATE_QUERY',
  EVENT_ESCALATION: 'EVENT_ESCALATION',
  CLOSE_ESCALATION: 'CLOSE_ESCALATION',
  TIMER_SYNC: 'TIMER_SYNC',
  TIMER_CLEAR: 'TIMER_CLEAR',
  /** Sibling signed in — peers restore shared session and leave /login. */
  SESSION_LOGIN: 'SESSION_LOGIN',
  /** Sibling signed out — peers clear shared session and return to /login. */
  SESSION_LOGOUT: 'SESSION_LOGOUT',
  /** Sync CTA — raise an already-open sibling tab without opening a new one. */
  SIBLING_FOCUS: 'SIBLING_FOCUS',
  /** Peer closed this event — drop that tab and toast. */
  EVENT_CLOSED: 'EVENT_CLOSED',
  /** Peer parked this event — drop that tab and toast. */
  EVENT_PARKED: 'EVENT_PARKED',
} as const;

export type AppEventTypes = (typeof AppEventTypes)[keyof typeof AppEventTypes];

const APP_EVENT_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(AppEventTypes));

export function isAppEventType(value: unknown): value is AppEventTypes {
  return typeof value === 'string' && APP_EVENT_TYPE_VALUES.has(value);
}

export interface AppEvent {
  /** Id of event to track event and order */
  id?: string;
  /** Frontend agent id that sent message */
  agentId?: string;
  /** Type of event */
  type: AppEventTypes;
  /** State of page */
  pageState?: PageSyncState;
  /** Per-tab processing timer state */
  processingTimer?: ProcessingTimerSyncState;
  /** Site/location name for close/park toasts */
  label?: string;
  /** Park duration text, e.g. "5 minutes" */
  durationLabel?: string;
}
